import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, Button, Box, Typography, TextField, MenuItem } from '@mui/material';
import { Add } from '@mui/icons-material';
import { EditableGrid } from '../../components/DataTable/EditableGrid';
import { ActionCell } from '../../components/DataTable/customcell/ActionCell';
import { EditDescriptor } from '@progress/kendo-react-data-tools';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { RootState } from '../../store';
import { validatePricePeriodsGridData } from "../Modals/action-bill-modal/util";
import { fetchSeedsUnitsDropdownPeriods } from '../../slices/productcostapplication.slice';
import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { saveApplicationGridData } from '../../slices/productcostseeds.slice';
import { useAddCost } from '../../hooks/useAddCost';
import { ApplicationActivityT } from '../../types/addCost';
import type { KeyboardEvent } from "react";
interface Period {
  firstApplied: Date;
  lastApplied: Date;
  costPerAcre: Number;
  notes: string;
  id: string;
}

interface ApplicationItem {
  id: number;
  workName: string;
  machineName: string;
  periods: Period[];
  rateType: string;
}

interface EditModalProps {
  isOpen: boolean;
  editItem: ApplicationItem | null;
  handleClose: () => void;
  isCreate?: boolean;
}

const EditApplicationModal: React.FC<EditModalProps> = ({
  isOpen,
  editItem,
  handleClose,
  isCreate 
}) => {
  const dispatch = useAppDispatch();
  const { handleAddApplicationActivity } = useAddCost();
  const { rateTypesArray } = useAppSelector((state: RootState) => state.seedsdropdown);
 
  const [rateTypeValue, setRateTypeValue] = useState<string>(editItem?.rateType || "");
  const [workName, setWorkName] = useState<string>(editItem?.workName || "");
  const [machineName, setMachineName] = useState<string>(editItem?.machineName || "");
  const [edit, setEdit] = useState<EditDescriptor>({});
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [periodsData, setPeriodsData] = useState<any[]>(
    editItem?.periods || []
  );
  const workNameInputRef = useRef<HTMLInputElement | null>(null);
  const machineNameInputRef = useRef<HTMLInputElement | null>(null);
  const lastFocusedFieldRef = useRef<"workName" | "machineName" | null>(null);

  useEffect(() => {
    dispatch(fetchSeedsUnitsDropdownPeriods({}));
  }, [dispatch]);

  const handleSaveClick = async () => {
    if (editItem) {
      const formattedPeriods = periodsData.map((item: any) => {
        return {
          firstApplied: moment(item.firstApplied).format("YYYY-MM-DD"),
          lastApplied: moment(item.lastApplied).format("YYYY-MM-DD"),
          costPerAcre: Number(item.costPerAcre) || 0,
          notes: item.notes
        };
      });

      if (isCreate) {
        if (!rateTypeValue || !workName.trim() || !machineName.trim()) {
          return;
        }

        const createPayload: ApplicationActivityT = {
          activity: workName.trim(),
          machineName: machineName.trim(),
          rateTypeMappings: [
            {
              rateType: rateTypeValue,
              periods: formattedPeriods,
            },
          ],
        };

        const isSuccess = await handleAddApplicationActivity(createPayload);
        if (isSuccess) {
          handleClose();
        }
        return;
      }

      const { ...updatedItemWithoutCropName } = editItem;
      const updatedItem = {
        ...updatedItemWithoutCropName,
        periods: formattedPeriods,
      };
      await dispatch(saveApplicationGridData(updatedItem));
      handleClose();
    }
  };
  
  useEffect(() => {
    setErrorFields(
      validatePricePeriodsGridData(periodsData, {
        firstApplied: "First Applied Date",
        lastApplied:  "Last Applied Date",
        costPerAcre: "Cost Per Acre",
        notes: "Notes"
      })
    );
    setEdit(
      periodsData.reduce((acc, item) => {
        acc[item.id] = true;
        return acc;
      }, {})
    );
  }, [periodsData]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (editItem) {
      setRateTypeValue(editItem.rateType || "");
      setWorkName(editItem.workName || "");
      setMachineName(editItem.machineName || "");
      setPeriodsData(
        editItem.periods.map((item: any) => {
          return {
            ...item,
            id: uuidv4(),
            costPerAcre: item.costPerAcre,
            firstApplied: moment(item.firstApplied, "MM/DD/YYYY").toDate(),
            lastApplied: moment(item.lastApplied, "MM/DD/YYYY").toDate(),
            notes: item.notes,
          };
        })
      );
    } else {
      setRateTypeValue("");
      setWorkName("");
      setMachineName("");
      setPeriodsData([]);
    }
  }, [isOpen, isCreate, editItem?.id]);

  const canSave = !!(
    periodsData.length &&
    !errorFields.length &&
    rateTypeValue &&
    workName.trim() &&
    machineName.trim()
  );

  useLayoutEffect(() => {
    if (!isOpen || !canSave) {
      return;
    }

    if (document.activeElement === document.body) {
      if (lastFocusedFieldRef.current === "workName") {
        workNameInputRef.current?.focus();
      }
      if (lastFocusedFieldRef.current === "machineName") {
        machineNameInputRef.current?.focus();
      }
    }
  }, [isOpen, canSave]);

  const addNewPeriod = () => {
    const newPeriod: Period = {
      firstApplied: new Date(),
      lastApplied: new Date(),
      costPerAcre: 0.0,
      notes: "",
      id: uuidv4()
    };

    setPeriodsData((prevData) => [...prevData, newPeriod]);
  };

  const handleHeaderInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const onItemChange1 = useCallback((e: any) => {
    const { field, value, dataItem } = e;
    setPeriodsData((prevData) =>
      prevData.map((item: any) => {
        if (item.id === dataItem.id) {
          const updatedRow = {
            ...item,
          };
          updatedRow[field] = value;
          return updatedRow;
        }
        return item;
      })
    );
  }, []);

  const deletePricePeriod2 = useCallback((dataItem: any) => {
    setPeriodsData((prevData) => prevData.filter((item: any) => item.id !== dataItem.id));
  }, []);

  const gridColumns: any[] = useMemo(
    () => [
      {
        field: "costPerAcre",
        title: "Cost Per Acre",
        editor: "numeric",
      },
      {
        field: "firstApplied",
        title: "First Applied Date",
        editor: "date",
        format: "{0:d}",
      },
      {
        field: "lastApplied",
        title: "Last Applied Date",
        editor: "date",
        format: "{0:d}",
      },
      {
        field: "notes",
        title: "Notes",
        editor: "text",
      },
      {
        title: "Edit Actions",
        cells: {
          data: (props: any) => (
            <ActionCell {...props} onDelete={deletePricePeriod2} />
          ),
        },
      },
    ],
    [deletePricePeriod2]
  );

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      sx={{ "& .MuiDialog-paper": { width: "60%", maxWidth: "none" } }}
    >
      <DialogTitle>{ isCreate ? "Create Application Product" : "Edit Application Product"}</DialogTitle>
      <DialogContent>
        {editItem && (
          <Box display={"flex"} flexDirection={"column"} gap={5}>
            <Box display={"flex"} flexWrap={"wrap"} gap={2} mt={2}>
              {isCreate ? (
                <TextField
                  select
                  label="Rate Type"
                  value={rateTypeValue}
                  sx={{ minWidth: 250 }}
                  onChange={(event) => setRateTypeValue(event.target.value)}
                >
                  {rateTypesArray.map((rateObj) => (
                    <MenuItem key={rateObj.id} value={rateObj.name}>
                      {rateObj.name}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  label="Rate Type"
                  value={editItem.rateType}
                  sx={{ minWidth: 250 }}
                  InputProps={{ readOnly: true }}
                />
              )}
              <TextField
                label="Work Name"
                sx={{ minWidth: 250 }}
                value={workName}
                inputRef={workNameInputRef}
                onFocus={() => {
                  lastFocusedFieldRef.current = "workName";
                }}
                onKeyDown={handleHeaderInputKeyDown}
                onChange={(event) => {
                  setWorkName(event.target.value);
                  const el = workNameInputRef.current;
                  if (el) {
                    const pos = el.selectionStart;
                    requestAnimationFrame(() => {
                      if (document.activeElement !== el) {
                        el.focus();
                        try { el.setSelectionRange(pos, pos); } catch (_) {}
                      }
                    });
                  }
                }}
                InputProps={{ readOnly: !isCreate }}
              />
              <TextField
                label="Machine Name"
                value={machineName}
                sx={{ minWidth: 250 }}
                inputRef={machineNameInputRef}
                onFocus={() => {
                  lastFocusedFieldRef.current = "machineName";
                }}
                onKeyDown={handleHeaderInputKeyDown}
                onChange={(event) => {
                  setMachineName(event.target.value);
                  const el = machineNameInputRef.current;
                  if (el) {
                    const pos = el.selectionStart;
                    requestAnimationFrame(() => {
                      if (document.activeElement !== el) {
                        el.focus();
                        try { el.setSelectionRange(pos, pos); } catch (_) {}
                      }
                    });
                  }
                }}
                InputProps={{ readOnly: !isCreate }}
              />
            </Box>
            <Box>
              <Box
                display="flex"
                justifyContent="space-between"
                mb={1}
                alignItems="center"
              >
                <Typography>Price Periods</Typography>
                <Button
                  type="button"
                  variant="contained"
                  color="primary"
                  startIcon={<Add />}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={addNewPeriod}
                >
                  Add Period
                </Button>
              </Box>
              <EditableGrid
                dataItemKey="id"
                onItemChange={onItemChange1}
                edit={edit}
                columns={gridColumns}
                data={periodsData}
              />
            </Box>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveClick}
                disabled={!canSave}
              >
                Save
              </Button>
              <Button variant="outlined" color="error" onClick={handleClose}>
                Cancel
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditApplicationModal;